import React from 'reactn';
import { Box, List, ListItem } from "@material-ui/core";
import { DateRangePicker } from 'materialui-daterange-picker';
import { withStyles } from '@material-ui/styles';
import moment from 'moment';
import { Snackbar } from '../../v2/shared/components/MaterialUIComponents';
import styles from './index.styles';
import { MAX_DAYS_ALLOWED, SNACK_BAR_TIMEOUT } from '../../v2/services/constants';
import { CustomIcons, icons } from '../../v2/shared/components/custom-icons';
import { Typography } from '@orijinworks/frontend-commons';
import {
    toDateRangePickerInitial,
    toLegacyRollingPresetRange,
    toUtcInclusivePresetRange,
    resolveInclusiveCustomFilterDateRange
} from '../../util/dateRangeUtils';
import { withUnleash } from '../../v2/core/unleash';
import { UnleashService } from '../../v2/services';

/**
 * @name initiateDataFetch
 * @param {object} range
 * @param {callback} setData
 * @param {callback} setDateRangeVisiblity (optional) only required when needed to toggle off
 * @desc Initiates data fetch either from cache / API endpoint.
 * @return {void}
 */
const initiateDataFetch = (range, dateChangeHandler, setDateRangeVisiblity) => {
    React.setGlobal({
       range 
    }, () => {
        if (dateChangeHandler) {
            dateChangeHandler(range);
        }
        if (setDateRangeVisiblity) {
            setDateRangeVisiblity(false);
        }

    });
};

const DaysFilter = (props) => {
    const { classes, unleashProps } = props;
    const isInclusiveEnabled = () => !!unleashProps?.isFlagEnabled?.();
    // Outcomes custom ranges set global.isCustomDateRange; keep the Custom chip in sync.
    const [isCustom, setCustom] = React.useState(!!React.getGlobal().isCustomDateRange);
    const [showDateRange, setDateRangeVisiblity] = React.useState(false);
    const [snackBar, setSnackBar] = React.useState(false);
    const initialDateRange = toDateRangePickerInitial(React.getGlobal().range);
    const pickerKey = initialDateRange
        ? `${initialDateRange.startDate.getTime()}-${initialDateRange.endDate.getTime()}`
        : 'empty';

    /**
     * Preset chips must match Outcomes when the inclusive FF is on
     * (UTC calendar-day bounds), otherwise learner ToT re-clicks diverge from the
     * initial load that reused global.range from Outcomes.
     */
    const getRangeData = (daysCount) => {
        React.setGlobal({
            selectedDateRange: daysCount,
            isCustomDateRange: false
        });
        return isInclusiveEnabled()
            ? toUtcInclusivePresetRange(daysCount)
            : toLegacyRollingPresetRange(daysCount);
    };

    const handleDateChange = (range) => {
        if (range.startDate && range.endDate) {
            const daysApart = Math.abs((range.endDate - range.startDate) / (1000 * 60 * 60 * 24));
            if (daysApart > MAX_DAYS_ALLOWED) {
                setSnackBar(true);
            } else {
                setSnackBar(false);
            }
        }

        setCustom(true);

        if (isInclusiveEnabled()) {
            const { formattedRange, daysCount } = resolveInclusiveCustomFilterDateRange(range);
            React.setGlobal({
                selectedDateRange: daysCount,
                isCustomDateRange: true
            });
            initiateDataFetch(formattedRange, props.dateChangeHandler, setDateRangeVisiblity);
            return;
        }

        const dates = {
            startDate: moment.utc(moment(range.startDate).format("YYYY-MM-DD")).toISOString(),
            endDate: moment.utc(moment(range.endDate).format("YYYY-MM-DD")).toISOString(),
        };
        React.setGlobal({ isCustomDateRange: true });
        initiateDataFetch(dates, props.dateChangeHandler, setDateRangeVisiblity);
    };

    const selectPreset = (daysCount) => {
        setCustom(false);
        React.setGlobal({
            selectedDateRange: daysCount
        });
        initiateDataFetch(getRangeData(daysCount), props.dateChangeHandler);
    };

    return (
        <Box
            display="flex"
            position="relative"
            alignItems="center"
            justifyContent="center"
            className={classes.actionBar}>
            <List className={classes.daysSelectionList}>
                <ListItem
                    button
                    onClick={() => selectPreset(7)}
                    className={[
                        classes.daysSelectionItem,
                        React.getGlobal().selectedDateRange === 7 && !isCustom ? classes.activeDay : null
                    ].join(" ")}
                >
                    7 Days
                </ListItem>
                <ListItem
                    onClick={() => selectPreset(30)}
                    button
                    className={[
                        classes.daysSelectionItem,
                        React.getGlobal().selectedDateRange === 30 && !isCustom ? classes.activeDay : null
                    ].join(" ")}
                >
                    30 Days
                </ListItem>
                <ListItem
                    onClick={() => selectPreset(90)}
                    button
                    className={[
                        classes.daysSelectionItem,
                        React.getGlobal().selectedDateRange === 90 && !isCustom ? classes.activeDay : null
                    ].join(" ")}
                >
                    90 Days
                </ListItem>
                <ListItem
                    onClick={() => selectPreset(365)}
                    button
                    className={[
                        classes.daysSelectionItem,
                        React.getGlobal().selectedDateRange === 365 && !isCustom ? classes.activeDay : null
                    ].join(" ")}
                >
                    365 Days
                </ListItem>
                <ListItem
                    onClick={() => {
                        setCustom(true);
                        React.setGlobal({ isCustomDateRange: true });
                        setDateRangeVisiblity(true);
                    }}
                    button
                    className={[
                        classes.daysSelectionItem,
                        isCustom
                            ? classes.activeDay : null
                    ].join(" ")}
                >
                    Custom
                </ListItem>
            </List>
            <Box
                position="absolute"
                zIndex="999"
                top="0px"
                left={props.dateRangePickerProps.left}
            >
                {/* Mount only while open so initialDateRange is taken from the current global filter. */}
                {showDateRange && (
                    <DateRangePicker
                        key={pickerKey}
                        open={true}
                        closeOnClickOutside={true}
                        maxDate={new Date()}
                        initialDateRange={initialDateRange}
                        toggle={() => setDateRangeVisiblity(false)}
                        onChange={handleDateChange}
                    />
                )}
            </Box>
            <Snackbar
                open={snackBar}
                autoHideDuration={SNACK_BAR_TIMEOUT}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setSnackBar(false)}
            >
                <Box className={classes.dateSnackbar}>
                    <CustomIcons icon={icons.WarningIcon} className={classes.warningIcon} />
                    <Typography className={classes.dateAlert} variant='p'>
                        Due to high messaging data volume, we strongly recommend retrieving no more than 1 year of records at a time.
                    </Typography>
                    <CustomIcons onClick={() => setSnackBar(false)} icon={icons.CloseIcon} className={classes.errorIcon}/>
                </Box>
            </Snackbar>
        </Box>
    );
};


export default withUnleash(
    withStyles(styles)(DaysFilter),
    UnleashService.FLAGS.INCLUSIVE_TOT_DATE_RANGE_FILTERS
);
